import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useUrlTab } from "../hooks/useUrlTab";
import { AppShell } from "../components/layout/AppShell";
import { NeuTabs } from "../components/neu/NeuTabs";
import { finalizePayroll } from "../api/payroll";
import { PayrollDashboard } from "../features/payroll/PayrollDashboard";
import { ReportsTab } from "../features/payroll/ReportsTab";
import { SettlementsTab } from "../features/payroll/SettlementsTab";
import { SalaryHoldsTab } from "../features/payroll/SalaryHoldsTab";
import { HoldsExportBar } from "../features/payroll/HoldsExportBar";
import { ExportButtons, EXPORT_TARGETS } from "../features/payroll/ExportButtons";
import { PayrollFilters } from "../features/payroll/PayrollFilters";
import { PayrollCycleCard } from "../features/payroll/PayrollCycleCard";
import { PayrollArchiveCard } from "../features/payroll/PayrollArchiveCard";
import { SealedMonthsCard } from "../features/payroll/SealedMonthsCard";
import { payrollTabs } from "../features/payroll/payrollTabs";
import { openPrintableReport } from "../features/payroll/openPrintable";
import { useReportBrand } from "../features/reports/useReportBrand";
import { useFeatures } from "../hooks/useFeatures";
import { useSavedScope } from "../hooks/useSavedScope";
import { usePayrollData } from "../features/payroll/usePayrollData";

export default function PayrollPage() {
  const { t, i18n } = useTranslation();
  const brand = useReportBrand();
  const { payroll_cycle: cycleOn } = useFeatures();
  const [tab, setTab] = useUrlTab("dashboard", "tab");
  const [filters, setFilters] = useSavedScope({
    month: new Date().toISOString().slice(0, 7), start: "", end: "",
  });
  const { run, progress, load } = usePayrollData(filters);
  const [reportKind, setReportKind] = useState("salary");
  const [busy, setBusy] = useState(false);
  const branchParam = filters.branch_id === "all" ? "" : filters.branch_id;

  const finalize = async () => {
    setBusy(true);
    try {
      const res = await finalizePayroll(filters.month);
      toast.success(t("payroll.finalized", { count: res.installments_deducted }));
      load();
    } catch {
      toast.error(t("errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell title={t("payroll.title")} subtitle={t("payroll.subtitle")}>
      <div className="grid gap-6" data-testid="payroll-page">
        <div className="flex justify-end" data-testid="payroll-export-bar">
          {tab === "holds" ? <HoldsExportBar /> : tab === "dashboard" || tab === "reports" ? (
            <ExportButtons filters={filters}
                           disabled={!(filters.branch_id && filters.user_id)}
                           onPdf={() => openPrintableReport(
                             tab === "reports" ? reportKind : "salary", filters, i18n.language, brand, t)}
                           {...EXPORT_TARGETS[tab === "reports" ? reportKind : "salary"]} />
          ) : null}
        </div>
        <NeuTabs tabs={payrollTabs(t, cycleOn)} value={tab} onChange={setTab} testid="payroll-tabs" />
        {tab === "reports" || tab === "holds" || tab === "settlements" ? null : <PayrollFilters value={filters} onChange={setFilters} />}

        {tab === "dashboard" ? (
          <PayrollDashboard run={run} progress={progress} busy={busy} onReload={load} onFinalize={finalize} filters={filters} />
        ) : null}

        {tab === "disburse" && cycleOn ? (
          <PayrollCycleCard month={filters.month} branchId={branchParam} />
        ) : null}

        {tab === "reports" ? (
          <ReportsTab filters={filters} onFiltersChange={setFilters}
                      kind={reportKind} onKindChange={setReportKind} />
        ) : null}

        {tab === "settlements" ? <SettlementsTab /> : null}

        {tab === "holds" ? <SalaryHoldsTab /> : null}

        {tab === "archive" ? (
          <>
            <SealedMonthsCard />
            <PayrollArchiveCard month={filters.month} branchId={branchParam} />
          </>
        ) : null}
      </div>
    </AppShell>
  );
}
